import React, { createContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import jwt_decode from "jwt-decode";
import axios from "axios";
import { clearPrefetchCache } from "../utils/tabPrefetch";

const AuthContext = createContext("");
export default AuthContext;

export const AuthProvider = ({ children }) => {
    const { t } = useTranslation();

    const [authTokens, setAuthTokens] = useState(() => {
        try {
            const storedTokens = localStorage.getItem('authTokens');
            return storedTokens ? JSON.parse(storedTokens) : null;
        } catch {
            return null;
        }
    });

    const [user, setUser] = useState(() => {
        try {
            const storedTokens = localStorage.getItem('authTokens');
            if (storedTokens) {
                const parsed = JSON.parse(storedTokens);
                const tokenStr = parsed?.access || storedTokens;
                return tokenStr ? jwt_decode(tokenStr) : null;
            }
        } catch {
            return null;
        }
        return null;
    });

    const [loader, setLoader] = useState(false);

    const history = useHistory();

    const loginUser = async (e) => {
        e.preventDefault();
        setLoader(true);

        try {
            const response = await axios.post("api/v1.0/token/", {
                email: e.target.email.value,
                password: e.target.password.value
            });
            setAuthTokens(response.data);
            try {
                setUser(jwt_decode(response.data.access));
            } catch {}
            try {
                localStorage.setItem('authTokens', JSON.stringify(response.data));
            } catch {}
            // a same-URL request cached from before this login (e.g. an
            // anonymous prefetch, or the previous account's session) must
            // not be handed back under the new identity
            clearPrefetchCache();
            history.push('/admin/tournaments');
            toast(t("You are successfully logged in"), { type: "success" });
        } catch (error) {
            setLoader(false);
            if (error.response?.status === 500) {
                history.push('/500');
            } else if (error.response?.data && 'non_field_errors' in error.response.data) {
                toast(t("To create a password, use the link in the mail"), { type: "error" });
            } else {
                toast(t("Wrong email or password"), { type: "error" });
            }
        } finally {
            setLoader(false);
        }
    };

    const logoutUser = () => {
        setAuthTokens(null);
        setUser(null);
        try {
            localStorage.removeItem('authTokens');
        } catch {}
        try {
            sessionStorage.clear();
        } catch {}
        clearPrefetchCache();
        history.push('/tournaments');
    };

    const socialLoginUser = async (provider, token, rawData = {}) => {
        setLoader(true);
        try {
            const response = await axios.post(`api/v1.0/auth/${provider}/`, { token, rawData });
            setAuthTokens(response.data);
            try {
                setUser(jwt_decode(response.data.access));
            } catch {}
            try {
                localStorage.setItem('authTokens', JSON.stringify(response.data));
            } catch {}
            clearPrefetchCache();
            history.push('/admin/tournaments');
            toast(t("You are successfully logged in"), { type: "success" });
        } catch (error) {
            toast(t("Social login failed"), { type: "error" });
        } finally {
            setLoader(false);
        }
    };

    const contextData = {
        user,
        setUser,
        authTokens,
        setAuthTokens,
        loginUser,
        socialLoginUser,
        logoutUser,
        loader
    };

    useEffect(() => {
        if (authTokens?.access) {
            try {
                setUser(jwt_decode(authTokens.access));
            } catch {}
        }
    }, [authTokens]);

    return (
        <AuthContext.Provider value={contextData}>
            {children}
        </AuthContext.Provider>
    );
};
